/**
 * Неблокирующий баннер «подтвердите email» (Фаза 3, И3).
 *
 * Показывается, когда пользователь залогинен, но `user.verified === false` (сразу
 * после регистрации, до клика по ссылке из письма). Текст + кнопка «Отправить письмо
 * снова» ([`resendVerification`](../useAuth.tsx), `POST /api/auth/resend-verification`).
 * Монтируется в `App` над контентом; не перекрывает интерактив (неблокирующий).
 *
 * i18n: текст со встроенной ссылкой на /privacy рендерится через <Trans>
 * (react-i18next), чтобы перевод оставался цельным предложением; email
 * подставляется интерполяцией, если есть.
 */
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from './ui';
import { useAuth } from '../useAuth';

export function UnverifiedBanner(): JSX.Element {
  const { t } = useTranslation();
  const { user, resendVerification } = useAuth();
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  async function handleResend(): Promise<void> {
    if (sending) return;
    setSending(true);
    setMsg(null);
    try {
      const res = await resendVerification();
      setMsg(res.status === 'already verified' ? t('verify.alreadyVerified') : t('verify.resent'));
    } catch {
      setMsg(t('verify.resendFailed'));
    } finally {
      setSending(false);
    }
  }

  const i18nKey = user?.email ? 'banner.withEmail' : 'banner.withoutEmail';

  return (
    <div className="verify-banner" role="status" data-testid="unverified-banner">
      <span className="verify-banner__text">
        <Trans
          i18nKey={i18nKey}
          components={[<Link to="/privacy" className="verify-banner__link" />]}
          values={{ email: user?.email ?? '' }}
        />
      </span>
      <button
        type="button"
        className="btn btn--ghost verify-banner__btn"
        onClick={() => void handleResend()}
        disabled={sending}
        data-testid="unverified-resend"
      >
        {sending ? t('common.submitting') : t('common.resendEmail')}
      </button>
      {msg ? <span className="verify-banner__msg">{msg}</span> : null}
    </div>
  );
}
